import React, { useState, useEffect, useMemo } from 'react';
import { Autocomplete, Box, Button, Drawer, makeStyles, StyledEngineProvider, StylesProviderV4, TextField, ThemeProvider, ThemeProviderV4, Typography } from '~/lib/bn-material-ui';
import { navTheme } from '~/shared/styles/nav-theme.js';
import { generateClassName, muiTheme } from '~/shared/styles/mui-theme.js';
import { sirenActions, useSirenEntity } from '~/runtime';
import { useTranslation } from 'react-i18next';
import { useChangeSelectedKey, useToggleHighlight, useTranslationsEditContextSelector } from './translations-edit-context';
import _ from 'lodash';
import { downloadBlobFile } from '~/shared/download-file';

const DRAWER_WIDTH = 400;

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: DRAWER_WIDTH,
  },
  drawerPaper: {
    width: DRAWER_WIDTH,
    marginTop: navTheme.nav.appBar.minHeight,
    padding: 16,
    backgroundColor: theme.palette.gray[0],
  },
  headerContainer: {
    '&.MuiBox-root': {
      marginBottom: 16,
    },
  },
  languageInfo: {
    padding: 8,
    backgroundColor: theme.palette.blue[40],
    borderRadius: 4,
    '& .MuiTypography-root': {
      color: theme.palette.gray[0],
    },
  },
  downloadButton: {
    '&.MuiButton-root': {
      marginBottom: 16,
    },
  },
  searchField: {
    '&.MuiTextField-root': {
      marginBottom: 16,
    },
  },
  spacing: {
    marginBottom: 16,
  },
  actionButtons: {
    display: 'flex',
    gap: 8,
    marginTop: 16,
  },
  originalValue: {
    fontStyle: 'italic',
  },
  highlighted: {
    backgroundColor: theme.palette.blue[40],
    color: theme.palette.gray[0],
    fontWeight: 'bold',
  },
}));

const SidebarHeader = ({ language, styles }) => {
  return (
    <Box className={styles.headerContainer}>
      <Typography variant='h6'>Translation Editor</Typography>
      <Box className={styles.languageInfo}>
        <Typography variant='body1'>Language: {language.toUpperCase()}</Typography>
      </Box>
    </Box>
  );
};

const DownloadButton = ({ isDownloading, onDownload, styles }) => {
  return (
    <Button
      variant='contained'
      color='primary'
      disabled={isDownloading}
      onClick={onDownload}
      className={styles.downloadButton}
      fullWidth
    >
      {isDownloading ? 'Downloading...' : 'Download Updated Translations File'}
    </Button>
  );
};

const SearchField = ({ searchTerm, onSearchTermChange, styles }) => {
  return <TextField
    label='Filter List by Values'
    placeholder='Type a translated value...'
    variant='outlined'
    InputLabelProps={{
      variant: 'outlined',
      shrink: true,
    }}
    value={searchTerm}
    onChange={onSearchTermChange}
    className={styles.searchField}
    fullWidth
    size='small'
  />;
};

const TranslationEditor = ({ editValue, onEditChange, styles }) => {
  return <TextField
    label='Translation Value'
    variant='outlined'
    InputLabelProps={{
      variant: 'outlined',
      shrink: true,
    }}
    value={editValue}
    onChange={onEditChange}
    className={styles.spacing}
    fullWidth
    multiline
    rows={4}
    placeholder='Enter translation value...'
  />;
};

const OriginalValueDisplay = ({ originalValue, styles }) => {
  return <Box>
    <Typography variant='body2' color='textSecondary'>Original Value:</Typography>
    <Typography variant='body2' className={styles.originalValue}>{originalValue}</Typography>
  </Box>;
};

const ActionButtons = ({ hasChanges, isSaving, isHighlightMode, onSave, onReset, onHighlightToggle, styles }) => {
  return <Box className={styles.actionButtons}>
    <Button
      variant='contained'
      onClick={onSave}
      disabled={!hasChanges || isSaving}
    >
      {isSaving ? 'Saving...' : 'Save'}
    </Button>
    <Button
      variant='outlined'
      onClick={onReset}
      disabled={!hasChanges || isSaving}
    >
      Reset
    </Button>
    <Button
      variant={isHighlightMode ? 'contained' : 'outlined'}
      className={isHighlightMode ? styles.highlighted : ''}
      onClick={onHighlightToggle}
    >
      {isHighlightMode ? '🔥 Highlighted' : 'Highlight'}
    </Button>
  </Box>;
};

// Flatten nested translation object into dot notation keys
function flattenTranslations(obj, prefix = '') {
  if (!obj) {
    return {};
  }
  return _.transform(obj, (result, value, key) => {
    const newKey = prefix ? `${prefix}.${key}` : key;

    if (_.isPlainObject(value)) {
      _.assign(result, flattenTranslations(value, newKey));
    } else {
      result[newKey] = value;
    }
  }, {});
}

function useTranslationData(i18n, searchTerm) {
  const translation = i18n.store?.data?.[i18n.language]?.translation;
  const flattenedTranslations = useMemo(() => flattenTranslations(translation), [translation]);

  if (!i18n.store?.data?.[i18n.language]?.translation) {
    return { translationKeys: [], flattenedTranslations: {} };
  }

  let allKeys = Object.keys(flattenedTranslations).sort();
  if (searchTerm) {
    allKeys = allKeys.filter((key) =>
      flattenedTranslations[key]?.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }
  return { translationKeys: allKeys, flattenedTranslations };
}

function useDownloadTranslations({ i18n }) {
  const [isDownloading, setIsDownloading] = useState(false);
  const handleDownload = async () => {
    setIsDownloading(true);
    const downloadData = i18n.store?.data?.[i18n.language]?.translation;
    await downloadBlobFile({ blob: JSON.stringify(downloadData), fileName: `translations-${i18n.language}.json` });
    setIsDownloading(false);
  };
  return { isDownloading, handleDownload };
}

function useSaveTranslation({ selectedKey, editValue, apiRoot, i18n, editMode }) {
  const [isSaving, setIsSaving] = useState(false);
  const language = i18n?.language;
  const translationBackend = i18n?.services?.backendConnector?.backend;

  const { entity: translationsEntity } = useSirenEntity({ href: `${apiRoot}/translations/${language}` });
  const translations = translationsEntity?.properties?.values;
  const saveTranslationAction = translationsEntity?.getAction('update-translations');

  // When we enter edit mode, we need to trigger the database translations to render.
  useEffect(() => {
    async function setEditModeAndDatabaseTranslations() {
      translationBackend?.setEditMode(editMode);
      if (editMode) {
        await translationBackend?.setDatabaseTranslations(language, translations);
      }
    }
    setEditModeAndDatabaseTranslations();
  }, [editMode, language, translations, translationBackend]);

  const handleSave = async () => {
    setIsSaving(true);
    _.set(translations, selectedKey, editValue);
    try {
      await sirenActions.submitAction(saveTranslationAction, { values: JSON.stringify(translations) }, { includeDefaults: false });
    } catch (error) {
      console.error(error);
    }
    setIsSaving(false);
  };
  return { isSaving, handleSave };
}

export function TranslationsEditSidebar() {
  const [editMode] = useTranslationsEditContextSelector(['editMode']);

  if (!editMode) {
    return null;
  }

  return <StyledEngineProvider generateClassName={generateClassName} injectFirst>
    <StylesProviderV4 generateClassName={generateClassName}>
      <ThemeProvider theme={muiTheme}>
        <ThemeProviderV4 theme={muiTheme}>
          <TranslationsEditSidebarInner/>
        </ThemeProviderV4>
      </ThemeProvider>
    </StylesProviderV4>
  </StyledEngineProvider>;
}

function TranslationsEditSidebarInner() {
  const styles = useStyles();
  const { i18n } = useTranslation();

  const [apiRoot, editMode, highlightOn, selectedKey] = useTranslationsEditContextSelector(['apiRoot', 'editMode', 'highlightOn', 'selectedKey']);
  const language = i18n?.language;

  const [editValue, setEditValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [originalValue, setOriginalValue] = useState('');
  const { translationKeys, flattenedTranslations } = useTranslationData(i18n, searchTerm);
  const { isDownloading, handleDownload } = useDownloadTranslations({ i18n });
  const { isSaving, handleSave } = useSaveTranslation({ selectedKey, editValue, apiRoot, i18n, editMode });

  const changeSelectedKey = useChangeSelectedKey();
  const handleTranslationKeyChange = (event, value) => {
    changeSelectedKey(value);
  };

  // Update edit values when selectedKey changes. This can be either from selecting from the Autocomplete, or clicking on a tooltip.
  useEffect(() => {
    const newValue = flattenedTranslations[selectedKey] ?? '';
    setEditValue(newValue);
    setOriginalValue(newValue);
  }, [selectedKey, flattenedTranslations]);

  const handleSearchTermChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleEditChange = (event) => {
    setEditValue(event.target.value);
  };

  const handleReset = () => {
    setEditValue(originalValue);
  };

  const toggleHighlight = useToggleHighlight();
  const handleHighlightToggle = () => {
    toggleHighlight();
  };

  const hasChanges = editValue !== originalValue;

  if (!editMode) {
    return null;
  }

  return (
    <Drawer
      className={styles.drawer}
      variant='persistent'
      anchor='right'
      open={editMode}
      PaperProps={{
        className: styles.drawerPaper,
      }}
    >
      <SidebarHeader language={language} styles={styles} />
      <DownloadButton
        isDownloading={isDownloading}
        onDownload={handleDownload}
        styles={styles}
      />

      <SearchField
        searchTerm={searchTerm}
        onSearchTermChange={handleSearchTermChange}
        styles={styles}
      />

      <Autocomplete
        options={translationKeys}
        getOptionLabel={(option) => option}
        renderInput={(params) => (
          <TextField
            {...params}
            label='Translation Key'
            variant='outlined'
            InputLabelProps={{
              ...params.InputLabelProps,
              variant: 'outlined',
              shrink: true,
            }}
          />
        )}
        onChange={handleTranslationKeyChange}
        className={styles.spacing}
        value={selectedKey ?? null}
      />

      <TranslationEditor
        editValue={editValue}
        onEditChange={handleEditChange}
        styles={styles}
      />
      {selectedKey && (
        <OriginalValueDisplay
          originalValue={originalValue}
          styles={styles}
        />
      )}
      <ActionButtons
        hasChanges={hasChanges}
        isSaving={isSaving}
        isHighlightMode={highlightOn}
        onSave={handleSave}
        onReset={handleReset}
        onHighlightToggle={handleHighlightToggle}
        styles={styles}
      />
    </Drawer>
  );
}
